import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import { definitionLabel } from 'styles/base-classes';
import ListLoader from 'components/utility/ContentLoader/ListLoader';
import { EntityContext } from 'context/entityContext';
import {
  ITopTerritoriesEntity,
  ITopTerritoriesProps,
  ITopTerritoriesEntityCountryItem,
  ITopTerritoriesEntityCityItem,
} from './types';

const TopTerritoriesWrap = styled.section<{
  compare: boolean;
  territoryType: string | undefined;
}>`
  display: flex;
  flex-flow: column nowrap;

  @media screen and (min-width: 100em) {
    padding-top: ${({ compare, territoryType }): string =>
      compare && territoryType === 'city' ? '1rem' : '0'};
  }
`;

const TopTerritoriesLabel = styled.h4`
  ${definitionLabel};
`;

const ListWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
`;
const ListWrapperColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopTerritoriesTotalList = styled.ol`
  padding: 0;
  margin-right: 1rem;
  list-style: none;
  color: ${({ theme }): string => theme.NightBlue};
`;

const TopTerritoriesCityList = styled(TopTerritoriesTotalList)`
  padding-bottom: 1em;
`;

const TopTerritoriesList = styled(TopTerritoriesTotalList)`
  &:not(:first-of-type) {
    [data-rank]::before {
      content: '';
      min-width: 0;
    }
  }
`;

export const TopTerritoriesItem = styled<any>('li')`
  display: flex;
  align-items: center;
  color: ${({ dataColor }: any): string => dataColor};
  font-weight: ${({ fontWeight }: any): string => fontWeight};

  &::before {
    content: '#' attr(data-rank);
    display: inline-block;
    min-width: 1.2rem;
    margin-right: 1rem;
    color: ${({ theme }): string => theme.NightBlue};
    font-weight: 400;
    text-align: right;
  }
`;

const LoaderWrapper = styled.div`
  svg {
    width: 100%;
    max-width: 9.375em;
  }
`;

/**
 * TopTerritories displays a list of top territories for each entity.
 *
 * @param {object} props React props
 * @param {function} props.getKey Returns a string to use as a React key
 * @param {string} props.label Label to display above the list
 * @param {boolean} props.pending Is the fetch for this component pending?
 * @param {Array.<object>} props.topTerritories Array of top territory data for each entity
 * @param {object} props.TerritoryItem React Component to use to display the territory items
 * @param {boolean} props.fullName Show Full name of territory
 */

const TopTerritories: React.FC<ITopTerritoriesProps> = (props) => {
  const {
    getKey,
    label,
    pending,
    topTerritories,
    TerritoryItem,
    type,
    fullName,
  } = props;

  const ctx = useContext(EntityContext);
  const { compareMode } = ctx;
  const List =
    type === 'city' && compareMode
      ? TopTerritoriesCityList
      : TopTerritoriesList;
  const Wrapper = type === 'city' ? ListWrapperColumn : ListWrapper;

  return (
    <TopTerritoriesWrap compare={!!compareMode} territoryType={type}>
      <TopTerritoriesLabel>{label}</TopTerritoriesLabel>

      <Wrapper>
        {pending && (
          <LoaderWrapper>
            <ListLoader
              len={5}
              itemWidth={150}
              itemHeight={16}
              itemMargin={8}
            />
          </LoaderWrapper>
        )}

        {!pending &&
          topTerritories &&
          topTerritories.map(
            (entity: ITopTerritoriesEntity, index: number): JSX.Element => (
              <List key={`${entity.entity.id}${index}`}>
                {entity.data.map(
                  (
                    item:
                      | ITopTerritoriesEntityCountryItem
                      | ITopTerritoriesEntityCityItem,
                    itemIndex: number
                  ): JSX.Element => (
                    <TerritoryItem
                      compareMode={compareMode}
                      entity={entity.entity}
                      rank={itemIndex + 1}
                      item={item}
                      key={getKey(entity, item)}
                      fullName={fullName}
                    />
                  )
                )}
              </List>
            )
          )}
      </Wrapper>
    </TopTerritoriesWrap>
  );
};
export default TopTerritories;
